import React, { useEffect } from 'react';
import {
	LoadingOverlay,
	Button,
	Title,
	Text,
	useMantineTheme,
	PasswordInput,
	Notification,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { validatePassword } from '@/utils/utilities';
import { HeroImg } from '@/components/partials/heroImg/HeroImg';
import { AppPaths } from '@/configs/appPaths';
import { ErrorCode, useContent } from '@/providers/ContentProvider';
import { useMutation } from '@tanstack/react-query';
import UnauthenticatedApi from '@/api/UnauthenticatedApi';
import styles from './SetNewPassword.module.scss';

const initialValues = {
	password: '',
	repeatPassword: '',
};

const baseSlug = 'setPassword.';

export const SetNewPassword: React.FC = () => {
	const { getErrorMessage, getContent, getMutationErrorMessage } = useContent();

	const { other } = useMantineTheme();

	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const token = searchParams.get('token');

	useEffect(() => {
		if (!token) navigate(`/${AppPaths.LOGIN}`);
	}, [navigate, token]);

	const form = useForm({
		initialValues,
		validate: {
			password: (value: string, values: typeof initialValues) => {
				if (value !== values.repeatPassword)
					return getErrorMessage([ErrorCode.DIFFERENT_PASSWORDS]);
				if (!validatePassword(value))
					return getErrorMessage([ErrorCode.PASSWORD_TOO_WEAK]);
				return null;
			},
			repeatPassword: (value: string, values: typeof initialValues) => {
				if (value !== values.password)
					return getErrorMessage([ErrorCode.DIFFERENT_PASSWORDS]);
				if (!validatePassword(value))
					return getErrorMessage([ErrorCode.PASSWORD_TOO_WEAK]);
				return null;
			},
		},
	});

	const newPasswordMutation = useMutation({
		mutationFn: UnauthenticatedApi.resetPassword,
		onSuccess: () => {
			form.reset();
		},
	});

	const setPasswordMutation = useMutation({
		mutationFn: UnauthenticatedApi.requestResetPassword,
		onSettled: () => {
			navigate('/set-password');
		},
	});

	const handleSubmit = form.onSubmit(async (values) => {
		if (!token) return;

		newPasswordMutation.mutate({
			password: values.password,
			hash: token,
		});
	});

	return (
		<div className="page-wrapper-unauth">
			<form className={styles.form} onSubmit={handleSubmit}>
				<LoadingOverlay visible={setPasswordMutation.isPending} />
				<Title ta="center" order={1} mb="md">
					{getContent(`${baseSlug}header`)}
				</Title>
				<Text size="lg" ta="center" c="dimmed" mb="xl">
					{getContent(`${baseSlug}subheader`)}
				</Text>
				{setPasswordMutation.isError && (
					<Notification {...other.notificationProps.danger} mb="lg">
						<Text size="md">
							{getMutationErrorMessage(setPasswordMutation.error)}
						</Text>
					</Notification>
				)}
				{setPasswordMutation.isSuccess && (
					<Notification {...other.notificationProps.success} mb="lg">
						<Text size="md">{getContent(`${baseSlug}success`)}</Text>
					</Notification>
				)}
				{newPasswordMutation.isSuccess && (
					<Notification {...other.notificationProps.success} mb="lg">
						<Text size="md">
							{getContent(`${baseSlug}confirmation1`)}{' '}
							<Text
								span
								c="blue"
								td="underline"
								fw={700}
								style={{ cursor: 'pointer' }}
								onClick={() => navigate('/login')}
							>
								{getContent(`${baseSlug}confirmation2`)}
							</Text>
						</Text>
					</Notification>
				)}
				{setPasswordMutation.isIdle && (
					<>
						<PasswordInput
							{...form.getInputProps('password')}
							size="lg"
							required
							placeholder={getContent(`${baseSlug}inputPasswordPlaceholder`)}
							mb="lg"
						/>
						<PasswordInput
							{...form.getInputProps('repeatPassword')}
							size="lg"
							required
							placeholder={getContent(
								`${baseSlug}inputRepeatPasswordPlaceholder`
							)}
							mb="lg"
						/>
						<Button type="submit" mb="lg">
							{getContent(`${baseSlug}submitBtn`)}
						</Button>
					</>
				)}
			</form>
			<HeroImg src={getContent(`${baseSlug}heroImg`)} />
		</div>
	);
};
